import {
  useCallback,
  useState,
} from 'react';

import type {
  ClientDuplicateCandidate,
} from '@/lib/clientDuplicateDecision';

type ConversionAction =
  | 'client'
  | 'case';

type LeadClientConversionOptions = {
  allowPossibleDuplicate?:
    boolean;
};

export type LeadConversionResult = {
  success: boolean;

  action:
    ConversionAction;

  reused:
    boolean;

  client?: {
    id: string;
    name: string;
  };

  case?: {
    id: string;
    [key: string]: unknown;
  };
};

export class LeadConversionRequestError
  extends Error {
  readonly status: number;
  readonly code: string | null;

  readonly duplicates:
    ClientDuplicateCandidate[];

  constructor(
    message: string,
    status: number,
    code: string | null,
    duplicates:
      ClientDuplicateCandidate[] =
      []
  ) {
    super(
      message
    );

    this.name =
      'LeadConversionRequestError';

    this.status =
      status;

    this.code =
      code;

    this.duplicates =
      duplicates;
  }
}

export function useLeadConversion() {
  const [
    conversionLeadId,
    setConversionLeadId,
  ] =
    useState(
      ''
    );

  const [
    conversionAction,
    setConversionAction,
  ] =
    useState<
      ConversionAction |
      null
    >(
      null
    );

  const runConversion =
    useCallback(
      async (
        leadId:
          string,

        action:
          ConversionAction,

        options:
          LeadClientConversionOptions =
          {}
      ) => {
        setConversionLeadId(
          leadId
        );

        setConversionAction(
          action
        );

        try {
          const response =
            await fetch(
              `/api/leads/${encodeURIComponent(
                leadId
              )}/convert`,
              {
                method:
                  'POST',

                headers: {
                  'Content-Type':
                    'application/json',
                },

                body:
                  JSON.stringify({
                    action,

                    allowPossibleDuplicate:
                      options.allowPossibleDuplicate ===
                      true,
                  }),
              }
            );

          const data =
            await response
              .json() as
                LeadConversionResult & {
                  error?: string;
                  code?: string;

                  duplicates?:
                    ClientDuplicateCandidate[];
                };

          if (
            !response.ok ||
            !data.success
          ) {
            throw new LeadConversionRequestError(
              data.error ||
                'Não foi possível converter o lead.',

              response.status,

              data.code ??
                null,

              Array.isArray(
                data.duplicates
              )
                ? data.duplicates
                : []
            );
          }

          return data;
        } finally {
          setConversionLeadId(
            ''
          );

          setConversionAction(
            null
          );
        }
      },
      []
    );

  const convertLeadToClient =
    useCallback(
      (
        leadId:
          string,

        options:
          LeadClientConversionOptions =
          {}
      ) =>
        runConversion(
          leadId,
          'client',
          options
        ),
      [
        runConversion,
      ]
    );

  const createCaseFromLead =
    useCallback(
      (
        leadId:
          string
      ) =>
        runConversion(
          leadId,
          'case'
        ),
      [
        runConversion,
      ]
    );

  return {
    conversionLeadId,
    conversionAction,

    convertLeadToClient,
    createCaseFromLead,
  };
}